import React from "react";
import { Stack, Typography, Divider, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";
import { useAppDispatch } from "../redux/store";
import { cleanFormData } from "../helpers/formHelpers";
import { updateSchool } from "../redux/actions/schoolActions";
import { schoolActions } from "../redux/slices/school";

import { SchoolResponseDto } from "../shared/dtos/school/SchoolResponseDto";
import { SchoolUpdateRequestDto } from "../shared/dtos/school/SchoolUpdateRequestDto";

const addressSchema = Yup.object().shape({
  streetLine1: Yup.string().required("Street Line 1 is required"),
  streetLine2: Yup.string().nullable(),
  city: Yup.string().required("City is required"),
  stateOrProvince: Yup.string().required("State/Province is required"),
  postalCode: Yup.string().required("Postal code is required"),
  country: Yup.string().required("Country is required"),
});

const validationSchema = Yup.object().shape({
  name: Yup.string().required("School name is required"),
  registrationNumber: Yup.string().required("Registration number is required"),
  contactEmail: Yup.string()
    .email("Invalid email format")
    .required("Contact email is required"),
  contactPhone: Yup.string().required("Contact phone is required"),
  vision: Yup.string().nullable(),
  mission: Yup.string().nullable(),
  motto: Yup.string().nullable(),
  website: Yup.string()
    .url("Invalid URL (must start with http:// or https://)")
    .nullable(),
  logoUrl: Yup.string().url("Invalid URL").required("Logo URL is required"),
  headOfficeAddress: addressSchema,
});

interface UpdateSchoolFormProps {
  school: SchoolResponseDto;
  loading: boolean;
  error?: string | null;
}

const UpdateSchoolForm: React.FC<UpdateSchoolFormProps> = ({
  school,
  loading,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  const initialValues: SchoolUpdateRequestDto = {
    name: school?.name || "",
    registrationNumber: school?.registrationNumber || "",
    vision: school?.vision || "",
    mission: school?.mission || "",
    motto: school?.motto || "",
    website: school?.website || "",
    logoUrl: school?.logoUrl || "",
    contactEmail: school?.contactEmail || "",
    contactPhone: school?.contactPhone || "",
    headOfficeAddress: {
      streetLine1: school?.headOfficeAddress?.streetLine1 || "",
      streetLine2: school?.headOfficeAddress?.streetLine2 || "",
      city: school?.headOfficeAddress?.city || "",
      stateOrProvince: school?.headOfficeAddress?.stateOrProvince || "",
      postalCode: school?.headOfficeAddress?.postalCode || "",
      country: school?.headOfficeAddress?.country || "Kenya",
    },
  };

  return (
    <Stack spacing={2} sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() => dispatch(schoolActions.setErrorUpdatingSchool(null))}
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          const rawCleaned = cleanFormData(values) as Record<string, any>;
          const cleanedValues: SchoolUpdateRequestDto = {
            name: rawCleaned.name,
            registrationNumber: rawCleaned.registrationNumber,
            vision: rawCleaned.vision || "",
            mission: rawCleaned.mission || "",
            motto: rawCleaned.motto || "",
            website: rawCleaned.website || null,
            logoUrl: rawCleaned.logoUrl,
            contactEmail: rawCleaned.contactEmail,
            contactPhone: rawCleaned.contactPhone,
            headOfficeAddress: {
              streetLine1: rawCleaned.headOfficeAddress.streetLine1,
              streetLine2: rawCleaned.headOfficeAddress.streetLine2 || null,
              city: rawCleaned.headOfficeAddress.city,
              stateOrProvince: rawCleaned.headOfficeAddress.stateOrProvince,
              postalCode: rawCleaned.headOfficeAddress.postalCode,
              country: rawCleaned.headOfficeAddress.country,
            },
          };

          dispatch(updateSchool(school.id, cleanedValues));
        }}
        enableReinitialize
      >
        {({ dirty, isValid }: FormikProps<SchoolUpdateRequestDto>) => (
          <Form>
            <Stack spacing={2}>
              <Typography
                variant="subtitle2"
                sx={{
                  textAlign: "left",
                  fontWeight: 700,
                  color: "text.secondary",
                }}
              >
                Basic Information
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" },
                  gap: 2,
                }}
              >
                <FormTextField name="name" label="School Name" />
                <FormTextField
                  name="registrationNumber"
                  label="Registration No."
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormTextField
                  name="contactEmail"
                  label="Contact Email"
                  type="email"
                />
                <FormTextField name="contactPhone" label="Contact Phone" />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormTextField name="website" label="Website URL" />
                <FormTextField name="logoUrl" label="Logo URL" />
              </Box>

              <Divider sx={{ my: 1 }} />

              <Typography
                variant="subtitle2"
                sx={{
                  textAlign: "left",
                  fontWeight: 700,
                  color: "text.secondary",
                }}
              >
                Identity & Vision
              </Typography>

              <FormTextField name="motto" label="School Motto" />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormTextField
                  name="vision"
                  label="Vision Statement"
                  multiline
                  rows={2}
                />
                <FormTextField
                  name="mission"
                  label="Mission Statement"
                  multiline
                  rows={2}
                />
              </Box>

              <Divider sx={{ my: 1 }} />

              <Typography
                variant="subtitle2"
                sx={{
                  textAlign: "left",
                  fontWeight: 700,
                  color: "text.secondary",
                }}
              >
                Head Office Address
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormTextField
                  name="headOfficeAddress.streetLine1"
                  label="Street Line 1"
                />
                <FormTextField
                  name="headOfficeAddress.streetLine2"
                  label="Street Line 2 (Optional)"
                />
                <FormTextField name="headOfficeAddress.city" label="City" />
                <FormTextField
                  name="headOfficeAddress.stateOrProvince"
                  label="State / Province"
                />
                <FormTextField
                  name="headOfficeAddress.postalCode"
                  label="Postal Code"
                />
                <FormTextField
                  name="headOfficeAddress.country"
                  label="Country"
                />
              </Box>

              <SubmitButton
                startIcon={<SaveOutlinedIcon />}
                disabled={!dirty || !isValid || loading}
                loading={loading}
                title="Save Changes"
                loadingText="Saving..."
                sx={{ mt: 2 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default UpdateSchoolForm;
